import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../constants/AppTable';
import { LoadingSpinner } from '../../../constants/AppLoading';
import DialogBox from '../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../constants/InputTextBox';
import { readerEditValidationSchema, readerValidationSchema, toleValidationSchema } from '../../../helpers/validation';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../helpers/handleChange';
import { EndBaseLine } from '../../../constants/AppSpace';
import { Button } from '../../../constants/Button';
import { putDataToTheServer } from '../../../helpers/api';
import AppURL from '../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../constants/Toast';
import { Reader } from '../../../helpers/models/Models';

interface ReaderListViewProps {
  data: {
    results: Reader[];
    previous: string | null;
    next: string | null;
  } | null;
  loading: boolean;
  error: string | null;
  setOnURL: React.Dispatch<React.SetStateAction<string>>;
  handleFormSuccess: () => void;
}

const ReaderListView: React.FC<ReaderListViewProps> = ({ data, loading, error, handleFormSuccess, setOnURL }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<Reader | null>(null);

  const columns = [
    { header: 'SN', accessor: 'sn', sortable: false },
    { header: 'Reader Name', accessor: 'reader_name', sortable: true },
    { header: 'Reader Name(Nepali)', accessor: 'reader_name_nep', sortable: false },
    { header: 'Reader Address', accessor: 'reader_add', sortable: false },
    { header: 'Reader Address(Nepali)', accessor: 'reader_add_nep', sortable: false },
    { header: 'Phone', accessor: 'reader_phone', sortable: false },
    { header: 'Username', accessor: 'username', sortable: false },
    { header: 'E-mail', accessor: 'email', sortable: false },
    { header: 'Create Date', accessor: 'reader_date', sortable: false },
    { header: 'Actions', accessor: 'actions', action: true },
  ];

  const modifiedData = useMemo(() => {
    if (!data) return [];
    return data.results.map((item: Reader) => ({
      sn: item.sn,
      reader_name: item.reader_name,
      reader_name_nep: item.reader_name_nep,
      reader_add: item.reader_add,
      reader_add_nep: item.reader_add_nep,
      reader_phone: item.reader_phone,
      email: item.email,
      username: item.username,
      branch: item.branch,
      user_no: item.user_no,
      reader_date: item.reader_date,
      reader_pass: item.reader_pass,
    }));
  }, [data]);

  const handleEdit = (item: Reader) => {
    setCurrentItem(item);
    setDialogOpen(true);
  };

  const formik = useFormik({
    initialValues: {
      reader_name: currentItem?.reader_name || "",
      reader_name_nep: currentItem?.reader_name_nep || "",
      reader_add: currentItem?.reader_add || "",
      reader_add_nep: currentItem?.reader_add_nep || "",
      reader_phone: currentItem?.reader_phone || "",
      email: currentItem?.email || "",
      username: currentItem?.username ||""
    },
    validationSchema: readerEditValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values, action) => {
      const formData = new FormData();
      formData.append("reader_name", values.reader_name);
      formData.append("reader_name_nep", values.reader_name_nep);
      formData.append("reader_add", values.reader_add);
      formData.append("username", values.username);
      formData.append("reader_add_nep", values.reader_add_nep);
      formData.append("reader_phone", values.reader_phone);
      formData.append("email", values.email);
      try {
        await putDataToTheServer(AppURL.FrontOfficeURL + 'front/reader/retrieve/and/update/view/' + currentItem?.sn, formData).then((res) => {
          if (res?.status === 200) {
            handleFormSuccess();
            setDialogOpen(false);
            ToastSuccess({ message: res?.message });
          } else if (res?.status === 400) {
            ToastWarning({ message: res?.message });
          }
          else {
            ToastWarning({ message: "Some Issue" });
          }
        });
      } catch (error) {
        console.log(error);
      }
    },
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <p>Error loading data: {error}</p>;
  }

  return (
    <Fragment>
      <AppTable
        columns={columns}
        data={modifiedData}
        onEdit={handleEdit}
        onNext={() => data?.next && setOnURL(data.next)}
        onPrev={() => data?.previous && setOnURL(data.previous)}
      />
      {dialogOpen && currentItem && (
        <DialogBox open={dialogOpen} headerName='Reader Edit Update' close={() => setDialogOpen(false)}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container columnSpacing={3} rowSpacing={0.8}>
              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <InputTextBox
                  type='text'
                  name='reader_name'
                  label='Reader Name'
                  handleChange={formik.handleChange}
                  value={formik.values.reader_name}
                  error={typeof formik.errors.reader_name === 'string' ? formik.errors.reader_name : undefined}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.reader_name}
                  required={true}
                  placeholder='Enter Reader Name'
                />
              </Grid>

              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <NepaliInputBox
                  label='Reader Name(Nepali)'
                  name='reader_name_nep'
                  value={formik.values.reader_name_nep}
                  handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'reader_name_nep' })}
                  required={true}
                  handleBlur={formik.handleBlur}
                  error={typeof formik.errors.reader_name_nep === 'string' ? formik.errors.reader_name_nep : undefined}
                  placeholder='Enter Marga/Tole nepali name'
                  touched={formik.touched.reader_name_nep}
                />
              </Grid>
              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <InputTextBox
                  type='text'
                  name='reader_add'
                  label='Reader Address'
                  handleChange={formik.handleChange}
                  value={formik.values.reader_add}
                  error={typeof formik.errors.reader_add === 'string' ? formik.errors.reader_add : undefined}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.reader_add}
                  required={false}
                  placeholder='Enter Reader Addres'
                />
              </Grid>
              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <NepaliInputBox
                  label='Addres Name(Nepali)'
                  name='reader_add_nep'
                  value={formik.values.reader_add_nep}
                  handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'reader_add_nep' })}
                  required={false}
                  handleBlur={formik.handleBlur}
                  error={typeof formik.errors.reader_add_nep === 'string' ? formik.errors.reader_add_nep : undefined}
                  placeholder='Enter Addres nepali'
                  touched={formik.touched.reader_name_nep}
                />
              </Grid>
              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <InputTextBox
                  type='text'
                  name='reader_phone'
                  label='Reader Phone'
                  handleChange={formik.handleChange}
                  value={formik.values.reader_phone}
                  error={typeof formik.errors.reader_phone === 'string' ? formik.errors.reader_phone : undefined}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.reader_phone}
                  required={false}
                  placeholder='Enter Reader Phone No'
                />
              </Grid>
              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <InputTextBox
                  type='email'
                  name='email'
                  label='Reader Email'
                  handleChange={formik.handleChange}
                  value={formik.values.email}
                  error={typeof formik.errors.email === 'string' ? formik.errors.email : undefined}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.email}
                  required={false}
                  placeholder='Enter Reader Email'
                />
              </Grid>
              <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                <InputTextBox
                  type='text'
                  name='username'
                  label='Username'
                  handleChange={formik.handleChange}
                  value={formik.values.username}
                  error={typeof formik.errors.username === 'string' ? formik.errors.username : undefined}
                  handleBlur={formik.handleBlur}
                  touched={formik.touched.username}
                  required={false}
                  placeholder='Enter Username'
                />
              </Grid>
              <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                <EndBaseLine top={18}>
                  <Button btn_type='update' type='submit' el='button' />
                </EndBaseLine>
              </Grid>
            </Grid>
          </form>
        </DialogBox>
      )}
    </Fragment>
  );
};

export default ReaderListView;
